import { useEffect, useRef } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Invisible scaffolder field extension that applies loaded infrastructure
 * config values to sibling fields in the current wizard step.
 *
 * It reads `loadedConfig` from formData (set by InfrastructureConfigLoader
 * in step 1), parses it, and sets matching field values via onChange on
 * the parent form.
 *
 * Renders nothing — this is a data-only component.
 */
export function InfrastructureConfigApplier(
  props: FieldExtensionComponentProps<string>,
) {
  const { formContext, schema } = props;
  const appliedRef = useRef(false);

  const formData = formContext?.formData as Record<string, unknown> | undefined;
  const loadedConfigStr = formData?.loadedConfig as string | undefined;

  useEffect(() => {
    // Only apply once per mount to avoid overwriting user changes
    if (appliedRef.current) return;
    if (!loadedConfigStr) return;

    let config: Record<string, unknown>;
    try {
      config = JSON.parse(loadedConfigStr) as Record<string, unknown>;
    } catch {
      return;
    }

    // Get the parent step schema to know which fields are in this step.
    // The formContext provides access to the RJSF form's onChange.
    // We use formContext to locate the parent form and set field values.
    //
    // In RJSF v5 the formContext is passed down. We look at the parent
    // step's schema properties to determine which keys belong here, then
    // call the formContext's onChange to update them.
    const parentSchema = (formContext as Record<string, unknown> | undefined)
      ?.schema as { properties?: Record<string, unknown> } | undefined;

    // Determine which keys belong to this step
    let stepKeys: string[];
    if (parentSchema?.properties) {
      stepKeys = Object.keys(parentSchema.properties);
    } else if (schema?.properties) {
      stepKeys = Object.keys(schema.properties as Record<string, unknown>);
    } else {
      stepKeys = [];
    }

    if (stepKeys.length === 0) return;

    // Build the updates: only set values for keys that exist in both the
    // loaded config and the current step schema, and that haven't already
    // been set in the form data (i.e. still at undefined or at the schema default).
    const updates: Record<string, unknown> = {};
    let hasUpdates = false;

    for (const key of stepKeys) {
      if (key.startsWith('_')) continue; // skip meta fields like _applyConfig
      if (config[key] === undefined) continue;

      const currentValue = formData?.[key];
      // Only apply if the field is empty/undefined or still at its default
      if (currentValue === undefined || currentValue === '') {
        updates[key] = config[key];
        hasUpdates = true;
      }
    }

    if (!hasUpdates) return;

    // Apply updates via formContext onChange if available
    const onFormChange = (formContext as Record<string, unknown> | undefined)
      ?.onChange as ((formData: Record<string, unknown>) => void) | undefined;

    if (onFormChange) {
      onFormChange({ ...formData, ...updates });
      appliedRef.current = true;
    }
  }, [loadedConfigStr, formContext, formData, schema]);

  // This component is invisible
  return null;
}
